import { getMarkRange } from "@tiptap/core";
import { Editor } from "@tiptap/react";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { copyTextToClipboard } from "../../ui-kit/kuika-copy-button/copy-text-to-clipboard";
import { promptDialog } from "./confirm-service";
import "./editor-link-menu.scss";

// A Jira-style hover menu for links inside a TipTap editor. Plain-click already opens the link
// (openOnClick), so this hover card is how you manage the destination without that conflict:
// Open / Edit / Copy / Remove, anchored under the hovered <a>.

interface HoveredLink {
  href: string;
  // Viewport rect of the anchor, for fixed-positioning the card under it.
  rect: DOMRect;
  // A document position inside the link, to resolve its mark range for edit/remove.
  pos: number;
}

interface EditorLinkMenuProps {
  editor: Editor | null;
}

function truncate(url: string): string {
  return url.length > 48 ? `${url.slice(0, 47)}…` : url;
}

const EditorLinkMenu: React.FC<EditorLinkMenuProps> = ({ editor }) => {
  const [hovered, setHovered] = useState<HoveredLink | null>(null);
  const hideTimer = useRef<number | null>(null);

  const cancelHide = (): void => {
    if (hideTimer.current !== null) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };
  const scheduleHide = (): void => {
    cancelHide();
    hideTimer.current = window.setTimeout(() => setHovered(null), 160);
  };

  useEffect(() => {
    if (!editor) return;
    const dom: HTMLElement = editor.view.dom as HTMLElement;

    const onOver = (event: MouseEvent): void => {
      const anchor: HTMLAnchorElement | null = (event.target as Element)?.closest?.("a") ?? null;
      if (!anchor || !dom.contains(anchor)) return;
      cancelHide();
      setHovered({
        href: anchor.getAttribute("href") ?? "",
        rect: anchor.getBoundingClientRect(),
        pos: editor.view.posAtDOM(anchor, 0)
      });
    };
    const onOut = (event: MouseEvent): void => {
      const anchor: HTMLAnchorElement | null = (event.target as Element)?.closest?.("a") ?? null;
      if (!anchor) return;
      // Don't hide if the pointer moved into the menu itself.
      const related: Element | null = event.relatedTarget as Element | null;
      if (related?.closest?.(".kuika-link-menu")) return;
      scheduleHide();
    };

    dom.addEventListener("mouseover", onOver);
    dom.addEventListener("mouseout", onOut);
    return () => {
      dom.removeEventListener("mouseover", onOver);
      dom.removeEventListener("mouseout", onOut);
      cancelHide();
    };
  }, [editor]);

  if (!editor || !hovered) return null;

  // Resolve the hovered link's mark range so edits/removal target exactly that link.
  const markRange = getMarkRange(editor.state.doc.resolve(hovered.pos), editor.schema.marks.link);
  const range: { from: number; to: number } | null = markRange ? { from: markRange.from, to: markRange.to } : null;

  const editHref = (): void => {
    const previous: string = hovered.href;
    setHovered(null);
    void promptDialog({
      title: "Link",
      label: "URL",
      placeholder: "https://",
      defaultValue: previous,
      confirmLabel: "Apply",
      allowEmpty: true,
      intent: "create",
      iconName: "link"
    }).then((next: string | null) => {
      if (next === null || !range) return;
      if (next === "") editor.chain().focus().setTextSelection(range).unsetLink().run();
      else editor.chain().focus().setTextSelection(range).setLink({ href: next }).run();
    });
  };
  const removeLink = (): void => {
    setHovered(null);
    if (range) editor.chain().focus().setTextSelection(range).unsetLink().run();
  };
  const copyLink = (): void => {
    void copyTextToClipboard(hovered.href);
    setHovered(null);
  };

  // Portal to <body>: the issue panel sets `will-change: transform`, which makes it the
  // containing block for fixed-position descendants — so a menu rendered inline would be offset
  // by the (right-side) panel's box instead of the viewport. In the body it's truly viewport-fixed.
  return createPortal(
    <div
      className="kuika-link-menu"
      style={{ position: "fixed", left: hovered.rect.left, top: hovered.rect.bottom + 6 }}
      onMouseEnter={cancelHide}
      onMouseLeave={scheduleHide}
    >
      <a className="kuika-link-menu__url" href={hovered.href} target="_blank" rel="noopener noreferrer nofollow">
        {truncate(hovered.href)}
      </a>
      <span className="kuika-link-menu__sep" />
      <button type="button" className="kuika-link-menu__action" onClick={editHref}>
        Edit
      </button>
      <button type="button" className="kuika-link-menu__action" onClick={copyLink}>
        Copy
      </button>
      <button type="button" className="kuika-link-menu__action kuika-link-menu__action--danger" onClick={removeLink}>
        Remove
      </button>
    </div>,
    document.body
  );
};

export default EditorLinkMenu;
